import type Map from 'ol/Map';
import type Geometry from 'ol/geom/Geometry';
import type LineString from 'ol/geom/LineString';
import Draw, { createBox } from 'ol/interaction/Draw';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import type { AppStore } from '../core/store';
import type { MapTool, SelectionEntry, VectorLayerModel, WorkingGeometryType } from '../core/types';
import { addWorkingFeature, replaceLayer } from '../domain/workingLayers';
import { DifferenceDrawingAssist, type DifferenceDrawAssistState } from './DifferenceDrawingAssist';
import type { LiveMeasure, LiveMeasureOptions } from './LiveMeasure';

type DrawingTool = Extract<MapTool, 'draw-point' | 'draw-line' | 'draw-polygon' | 'draw-rectangle'>;
export type PartialDrawingTool = Extract<DrawingTool, 'draw-polygon' | 'draw-rectangle'>;

interface PolygonCaptureOptions {
  readonly purpose?: 'partial' | 'difference';
  readonly cancel?: () => void;
  readonly targetGeometry?: Geometry;
  readonly assistStateChanged?: (state: DifferenceDrawAssistState) => void;
  /** V1.9.3 H: live measure card while drawing (partial areas: effective surface inside the parent). */
  readonly live?: LiveMeasureOptions;
}

interface BoundaryCutCaptureOptions {
  readonly cancel?: () => void;
  readonly targetGeometry: Geometry;
  readonly assistStateChanged?: (state: DifferenceDrawAssistState, started: boolean) => void;
}

const differenceSketchStyle = new Style({
  fill: new Fill({ color: 'rgba(255,0,168,.22)' }),
  stroke: new Stroke({ color: '#ff00a8', width: 4, lineDash: [10, 5] }),
  image: new CircleStyle({ radius: 6, fill: new Fill({ color: '#00e5ff' }), stroke: new Stroke({ color: '#16143d', width: 2 }) }),
});

const geometryTypeByTool: Record<DrawingTool, WorkingGeometryType> = {
  'draw-point': 'Point',
  'draw-line': 'LineString',
  'draw-polygon': 'Polygon',
  'draw-rectangle': 'Polygon',
};

const isDrawingTool = (tool: MapTool): tool is DrawingTool => tool in geometryTypeByTool;

export class DrawingController {
  private draw: Draw | null = null;
  private sketchActive = false;
  private transient = false;
  private transientComplete: ((geometry: Geometry) => void) | null = null;
  private transientCancel: (() => void) | null = null;
  private differenceAssist: DifferenceDrawingAssist | null = null;
  private boundaryCutStarted = false;
  private disposeLive: (() => void) | null = null;

  constructor(private readonly map: Map, private readonly store: AppStore, private readonly status: (text: string) => void, private readonly live: LiveMeasure | null = null) {
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.cancelSketch(); });
  }

  canActivate(tool: MapTool): boolean {
    if (!isDrawingTool(tool)) return true;
    const layer = this.activeLayer();
    return Boolean(layer && !layer.locked && layer.workingGeometryType === geometryTypeByTool[tool]);
  }

  setTool(tool: MapTool): boolean {
    this.removeInteraction();
    if (!isDrawingTool(tool)) return true;
    if (!this.canActivate(tool)) {
      this.status('Activa y desbloquea una capa de trabajo compatible.');
      return false;
    }
    const geometryType = geometryTypeByTool[tool];
    this.draw = new Draw({
      type: tool === 'draw-rectangle' ? 'Circle' : geometryType,
      stopClick: true,
      geometryFunction: tool === 'draw-rectangle' ? createBox() : undefined,
    });
    this.map.addInteraction(this.draw);
    if (geometryType !== 'Point') this.disposeLive = this.live?.attach(this.draw, { title: geometryType === 'LineString' ? 'Medida en vivo' : 'Medida en vivo' }) ?? null;
    this.draw.on('drawstart', () => { this.sketchActive = true; this.status('Dibujando… · Esc para cancelar'); });
    this.draw.on('drawabort', () => { this.sketchActive = false; });
    this.draw.on('drawend', ({ feature }) => {
      this.sketchActive = false;
      const geometry = feature.getGeometry();
      if (geometry) this.commitGeometry(geometry.clone());
    });
    this.status(`Dibujo activo · ${geometryType === 'Point' ? 'Punto' : geometryType === 'LineString' ? 'Línea' : tool === 'draw-rectangle' ? 'Rectángulo' : 'Polígono'}`);
    return true;
  }

  capturePolygon(tool: PartialDrawingTool, complete: (geometry: Geometry) => void, options: PolygonCaptureOptions = {}): void {
    this.removeInteraction();
    this.transient = true;
    this.transientComplete = complete;
    this.transientCancel = options.cancel ?? null;
    const differenceMode = options.purpose === 'difference';
    this.differenceAssist = differenceMode && options.targetGeometry
      ? new DifferenceDrawingAssist(options.targetGeometry, options.assistStateChanged)
      : null;
    this.draw = new Draw({
      type: tool === 'draw-rectangle' ? 'Circle' : 'Polygon',
      stopClick: true,
      geometryFunction: tool === 'draw-rectangle' ? createBox() : undefined,
      style: differenceMode ? differenceSketchStyle : undefined,
      trace: false,
      traceSource: this.differenceAssist?.source,
    });
    this.map.addInteraction(this.draw);
    this.differenceAssist?.attach(this.map, this.draw);
    if (!differenceMode) this.disposeLive = this.live?.attach(this.draw, options.live ?? { title: 'Medida en vivo' }) ?? null;
    this.draw.on('drawstart', () => { this.sketchActive = true; this.status(`${differenceMode ? 'Dibujando recorte Difference' : 'Dibujando área parcial'}… · Esc para cancelar`); });
    this.draw.on('drawabort', () => { this.sketchActive = false; });
    this.draw.on('drawend', ({ feature }) => {
      this.sketchActive = false;
      const geometry = feature.getGeometry()?.clone();
      const callback = this.transientComplete;
      this.clearTransientInteraction();
      if (geometry && callback) callback(geometry);
    });
    this.status(`${differenceMode ? 'Quitar área' : 'Área parcial'} · ${tool === 'draw-rectangle' ? 'Rectángulo' : 'Polígono'} · Esc para cancelar`);
  }

  /**
   * One transient drawing of any working geometry (planning objects): the caller decides what to do with it. Same
   * contract as capturePolygon: completes once, Esc or cancelSketch() calls `cancel`.
   */
  captureGeometry(kind: 'Point' | 'LineString' | 'Polygon' | 'Rectangle', complete: (geometry: Geometry) => void, options: { readonly cancel?: () => void; readonly label?: string } = {}): void {
    this.removeInteraction();
    this.transient = true;
    this.transientComplete = complete;
    this.transientCancel = options.cancel ?? null;
    this.draw = new Draw({ type: kind === 'Rectangle' ? 'Circle' : kind, stopClick: true, geometryFunction: kind === 'Rectangle' ? createBox() : undefined });
    this.map.addInteraction(this.draw);
    if (kind !== 'Point') this.disposeLive = this.live?.attach(this.draw, { title: options.label ?? 'Medida en vivo' }) ?? null;
    const what = options.label ?? 'objeto';
    this.draw.on('drawstart', () => { this.sketchActive = true; this.status(`Dibujando ${what}… · ${kind === 'LineString' || kind === 'Polygon' ? 'doble clic para terminar · ' : ''}Esc para cancelar`); });
    this.draw.on('drawabort', () => { this.sketchActive = false; });
    this.draw.on('drawend', ({ feature }) => {
      this.sketchActive = false;
      const geometry = feature.getGeometry()?.clone();
      const callback = this.transientComplete;
      this.clearTransientInteraction();
      if (geometry && callback) callback(geometry);
    });
    this.status(`${what} · ${kind === 'Point' ? 'un clic en el mapa' : kind === 'Rectangle' ? 'dos clics en esquinas opuestas' : 'clic en cada vértice, doble clic para terminar'} · Esc para cancelar`);
  }

  captureBoundaryCut(complete: (geometry: LineString) => void, options: BoundaryCutCaptureOptions): void {
    this.removeInteraction();
    this.transient = true;
    this.transientComplete = complete as (geometry: Geometry) => void;
    this.transientCancel = options.cancel ?? null;
    this.boundaryCutStarted = false;
    const emit = (state: DifferenceDrawAssistState): void => options.assistStateChanged?.(state, this.boundaryCutStarted);
    this.differenceAssist = new DifferenceDrawingAssist(options.targetGeometry, emit);
    this.draw = new Draw({
      type: 'LineString',
      stopClick: true,
      style: differenceSketchStyle,
      trace: false,
      traceSource: this.differenceAssist.source,
      condition: () => this.boundaryCutStarted || Boolean(this.differenceAssist?.getState().snapped),
      finishCondition: () => Boolean(this.differenceAssist?.getState().snapped),
    });
    this.map.addInteraction(this.draw);
    this.differenceAssist.attach(this.map, this.draw);
    this.draw.on('drawstart', () => {
      this.sketchActive = true;
      this.boundaryCutStarted = true;
      const state = this.differenceAssist?.getState();
      if (state) emit(state);
      this.status('Inicio ajustado al borde · dibuja la línea y finaliza sobre el borde · Esc para cancelar');
    });
    this.draw.on('drawabort', () => { this.sketchActive = false; this.boundaryCutStarted = false; });
    this.draw.on('drawend', ({ feature }) => {
      this.sketchActive = false;
      const geometry = feature.getGeometry()?.clone();
      const callback = this.transientComplete;
      this.clearTransientInteraction();
      if (geometry?.getType() === 'LineString' && callback) callback(geometry);
    });
    this.status('Corte al borde · ajusta el primer punto al límite del fragmento');
  }

  cancelSketch(): void {
    const hadSketch = this.sketchActive;
    this.draw?.abortDrawing();
    this.sketchActive = false;
    if (this.transient && this.draw) {
      const callback = this.transientCancel;
      this.clearTransientInteraction();
      callback?.();
    }
    if (hadSketch) this.status('Dibujo cancelado');
  }

  setDifferenceSnapEnabled(enabled: boolean): void {
    this.differenceAssist?.setSnapEnabled(enabled);
  }

  setDifferenceTraceEnabled(enabled: boolean): void {
    this.differenceAssist?.setTraceEnabled(enabled);
  }

  differenceAssistState(): DifferenceDrawAssistState | null {
    return this.differenceAssist?.getState() ?? null;
  }

  updateDifferenceTarget(targetGeometry: Geometry): void {
    this.differenceAssist?.updateTargetGeometry(targetGeometry);
  }

  private commitGeometry(geometry: Geometry): void {
    const state = this.store.getState();
    const layer = this.activeLayer();
    if (!layer || layer.locked) { this.status('No se creó el objeto: la capa de trabajo ya no está disponible.'); return; }
    try {
      const added = addWorkingFeature(layer, geometry);
      const entry: SelectionEntry = { source: 'generic', layerId: layer.id, kind: 'feature', id: added.record.id };
      this.store.update({
        layers: replaceLayer(state.layers, added.layer),
        selectionSet: { layerId: layer.id, entries: [entry], focus: entry },
        genericSelection: { layerId: layer.id, featureId: added.record.id },
        selection: null,
      });
      this.status(`Objeto creado · ${added.layer.features.length} en ${added.layer.name}`);
    } catch (error) {
      this.status(error instanceof Error ? error.message : 'No fue posible crear el objeto.');
    }
  }

  private activeLayer(): VectorLayerModel | null {
    const state = this.store.getState();
    return state.layers.find((layer) => layer.id === state.activeLayerId && layer.kind === 'working') ?? null;
  }

  private removeInteraction(): void {
    this.cancelSketch();
    this.disposeLive?.(); this.disposeLive = null;
    this.differenceAssist?.dispose();
    this.differenceAssist = null;
    if (this.draw) { this.map.removeInteraction(this.draw); this.draw = null; }
  }

  private clearTransientInteraction(): void {
    this.disposeLive?.(); this.disposeLive = null;
    this.differenceAssist?.dispose();
    this.differenceAssist = null;
    if (this.draw) this.map.removeInteraction(this.draw);
    this.draw = null;
    this.transient = false;
    this.transientComplete = null;
    this.transientCancel = null;
    this.boundaryCutStarted = false;
  }
}
